// The light of the sun and the sky at the eye, worked through the same cloud volume that is drawn (cloudVolume.ts).
//
// Why. The server lights the world through its cloud CELLS (half ellipsoids, one ray to the sun and a 48 ray dome from the middle of a 64 m patch). The picture is
// drawn from the cloud VOLUME, which is built from the same fields but realised as a continuous, lumpy density. The two agree on how much cloud there is and
// disagree on which rays are blocked: measured on one stored model state, they differ on whether the sun is blocked on about one ray in five at every sun height,
// so the disc, the cast shadows and the ambient light did not follow the cloud on screen. Here the optical depth on the ray to the sun and on a dome of rays is
// marched through the bytes the volume's texture holds (the blended, displayed state, moved by the cloud's frame), and the server's own cloud filter is applied
// to it:
//
//     direct = clear * tbh * e                                                                      e = exp(-tau) on the ray to the sun
//     sky    = open * mh * dome + clear * mu * (sc * dome + tbh * btr) + floor                       btr the share of the beam the cloud scatters down (cloudOptics.beamToDiffuse
//                                                                                                   at tau mu), dome the cosine weighted mean over the dome's rays of the share of the
//                                                                                                   sky's light the cloud passes (cloudOptics.diffuseTransmittance at tau_v = tau mu)
// Both come from the world's cloud optics (cloudOptics.ts), as colours; until they arrive `update` is not ready and the caller keeps the server's light.
//
// (the pieces clear, tbh, mh, sc, open, floor are made at the eye from the atmosphere's tables: eyeLight.ts.)
//
// This is a function of data (the volume's bytes and frame, the sun's direction, a place) and nothing else: no GPU, no shared memory. A server that renders for
// remote clients can run the same march at their places from the same message.
//
// The same rays give the ambient light a direction (`field`): the dome's rays are equal in solid angle, each with the transmittance it found, and the sky seen along it is
// the clear sky's brightness in that direction (the sky model's table, read through setShape) where the cloud lets it through and the cloud's own scattered light where it
// does not; below the horizon the ground has a radiance of its own. Projected onto spherical harmonics up to the second band (nine coefficients), that is what three's
// LightProbe takes. A face turned up gets exactly the flat ambient's light; one turned toward a clear patch, a bank of cloud or the ground gets what it sees.
//
// Cost. The beam is five rays (the sun's disc and a ring about it, so the shadow's edge has a penumbra and does not flip in a frame); the dome is 48 rays, a
// quarter of them redone at each step. The two are never done in the same frame. Measured in node on a real volume: a few tenths of a millisecond a piece (see the handoff).
// Nothing here touches the GPU.

import { planet, EARTH_RADIUS_M } from './planet';
import { cloudOptics, LIQUID } from './cloudOptics';

export const EARTH_R = EARTH_RADIUS_M;
export const DOME_RAYS = 48;
const GOLDEN = 2.399963229728653;

/** One level of the cloud windows (cloudVolume.ts): a window of n x n columns and nz layers in the stacked density array `vol` from byte `off` on (layer after layer, row = three z, column = three x),
 *  the square root of the extinction in units of extMax. cx, cz: its north-west corner (three axes, m); the world level wraps round the world's edge. */
export interface RingLayer {
  vol: Uint8Array; off: number;
  n: number; nz: number;
  dx: number; dz: number;      // the cell and the layer, metres
  cx: number; cz: number;      // the window's corner (x east, z south; metres)
  size: number;                // the window's edge, metres
  reach: number;               // how far from the eye it is read (m); the finest level that holds the point and reaches it is the one used
  wrap: boolean;               // the world level: periodic with period `worldM`
}

/** What the march reads: the displayed density of every level, finest first, the world level last. */
export interface VolView {
  layers: RingLayer[];
  worldM: number;              // the world's period (m)
  span: number;                // the height the layers cover (m above the sea)
  extMax: number;              // 1/m at byte 255
  steep: number;               // the exponent the picture puts on the density share (cloudVolume.ts STEEP_DEFAULT, 1.5 by default): the drawn cloud is more transparent than the extinction the server computed, and the light follows what is drawn
}

/** The moon, for `SkyLight.update`: unit vector toward it (three axes), whether it is above the horizon, its apparent radius (radians); `glow` how much of the sky's light is its (0 to 1: the sky's shape then has a glow about it); `march` whether to work out its beam through the cloud (five rays, while it is up). */
export interface MoonIn { dir: ArrayLike<number>; up: boolean; ang: number; glow: number; march: boolean }

/** The pieces of the cloud filter that do not depend on place. */
export interface Atm { open: ArrayLike<number>; floor: ArrayLike<number>; mh: number; sc: number; tbh: number }

const STEP_MS = 100;           // the beam and a share of the dome are redone this often
const DOME_PER_STEP = 12;      // dome rays redone per step (48 / 12 = every 400 ms a full dome)
const TAU_CAP_BEAM = 14;       // a ray this thick passes nothing (exp(-14) = 8e-7): stop marching
const RING_RAD = 0.0070;       // radians (0.4 degrees): the five beam rays are the sun's centre and four on a ring, about 1.5 times the disc's radius
const BEAM_SMOOTH_S = 0.12;    // the beam is eased this much between steps (a step is not a stair in the light)
const DOME_SMOOTH_S = 0.6;
const DOME_RANGE_M = 30000;    // the picture draws cloud out to 30 km; a dome ray stops there too (a ray near the horizon would otherwise cross hundreds of kilometres of tile)
const MOON_GLOW = 1.5;         // the moon's glow in the sky's shape, at the peak and for a moon that is all of the sky's light (the sun's own aureole in the sky model is of this order)
const MOON_GLOW_RAD = 0.35;    // its width, radians
const CLOUD_EPS = 0.15;        // a little of the cloud-lit light is spread over the whole dome, so the light scattered by a thin or distant cloud is not lost where the sky is open
const PI = Math.PI;

// The dome's rays: equal in solid angle (the height mu = sin(elevation) is stratified, the azimuth runs on the golden angle), so a sum over them is an integral over the
// sky and any weighting, the cosine of the horizontal surface (mu) or of a tilted one, is a weight on the ray. Ray i: its direction (x, mu, z).
/** The clear sky's shape: sine of elevation and azimuth from the sun in, a colour out. */
export type ShapeFn = (sinEl: number, phi: number, out: number[]) => void;
const RAY_MU = new Float32Array(DOME_RAYS), RAY_X = new Float32Array(DOME_RAYS), RAY_Z = new Float32Array(DOME_RAYS), RAY_AZ = new Float32Array(DOME_RAYS);
for (let i = 0; i < DOME_RAYS; i++) {
  const mu = (i + 0.5) / DOME_RAYS, st = Math.sqrt(1 - mu * mu), ph = i * GOLDEN;
  RAY_MU[i] = mu; RAY_X[i] = st * Math.cos(ph); RAY_Z[i] = st * Math.sin(ph); RAY_AZ[i] = Math.atan2(RAY_Z[i]!, RAY_X[i]!);
}

// fourteen faces (the six axes and eight corners) to check that the light on none is below zero
const FACE = new Float32Array(14 * 3);
{ let n = 0; for (const a of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]]) { FACE[n++] = a[0]!; FACE[n++] = a[1]!; FACE[n++] = a[2]!; }
  const r = Math.sqrt(1 / 3); for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) { FACE[n++] = sx * r; FACE[n++] = sy * r; FACE[n++] = sz * r; } }

/** The optical depth of the cloud in `v` on the ray from (ox, oy, oz) (three axes: x east, y up, z south; metres above the sea) along the unit vector (dx, dy, dz).
 *  The Earth curves away under the ray (a cloud at horizontal distance d is d^2 / 2R lower), as the server's old cloud-cell ray did. `jit` in [0, 1) offsets the samples (a fixed value
 *  per ray, so nothing flickers). Stops once the optical depth passes `cap`, or at path length `smax`. The ray starts at the eye: the level a sample is read from is the
 *  finest one that holds it and whose reach the path length is within, as the picture's own march chooses it (without the blend between levels). */
export function rayTau(v: VolView, lut: Float32Array, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, jit: number, cap: number, smax = 4e5): number {
  if (!(dy > 1e-6)) return 0;
  const layers = v.layers, nl = layers.length;
  if (nl === 0) return 0;
  const hz = Math.sqrt(dx * dx + dz * dz);
  const a = hz * hz / (2 * (planet.radius > 0 ? planet.radius : EARTH_R));         // the picture's own planet (planet.ts); the Earth's when the picture is flat
  const zlo = 0, zhi = v.span;
  // the path length at which the (curvature lifted) height reaches the slab's bottom and its top: the root of a s^2 + dy s + (oy - z) = 0, in a form that is good as a -> 0
  const rootAt = (c: number): number => { const q = dy * dy - 4 * a * c; return q <= 0 ? 4e5 : -2 * c / (dy + Math.sqrt(q)); };
  const s0 = oy >= zlo ? 0 : rootAt(oy - zlo);
  const s1 = Math.min(smax, oy >= zhi ? 0 : rootAt(oy - zhi));
  if (!(s1 > s0)) return 0;
  const W = v.worldM;
  const em = v.extMax, p2 = 2 * v.steep, lin = v.steep === 1, cube = p2 === 3;
  let sum = 0, s = s0;
  let first = true;
  while (s < s1) {
    // the step grows with distance: near the eye a cell is several samples, far out a ray crosses banks kilometres thick
    const ds = Math.min(400, Math.max(100, 0.015 * s));
    if (first) { s += jit * ds; first = false; if (!(s < s1)) break; }
    const h = oy + dy * s + a * s * s;
    if (h > 0 && h < zhi) {
      const x = ox + dx * s, z = oz + dz * s;
      let L: RingLayer | null = null, ex = 0, ez = 0;
      for (let i = 0; i < nl; i++) {                       // finest first
        const l = layers[i]!;
        if (l.wrap) { L = l; ex = x - l.cx; ez = z - l.cz; ex -= Math.floor(ex / W) * W; ez -= Math.floor(ez / W) * W; break; }
        if (s >= 0.875 * l.reach) continue;
        let px = x - l.cx, pz = z - l.cz;
        px -= Math.floor(px / W) * W; pz -= Math.floor(pz / W) * W;
        const m = 2 * l.dx;                                // (two cells in from the window's edge: the window might not hold what the edge shows)
        if (px >= m && px <= l.size - m && pz >= m && pz <= l.size - m) { L = l; ex = px; ez = pz; break; }
      }
      if (L !== null) {
        const n = L.n, per = n * n, nzm = L.nz - 1, vol = L.vol, off = L.off;
        let fi = ex / L.dx - 0.5, fj = ez / L.dx - 0.5, fk = h / L.dz - 0.5;
        if (fk < 0) fk = 0; else if (fk > nzm) fk = nzm;
        let i0 = Math.floor(fi), j0 = Math.floor(fj);
        const ti = fi - i0, tj = fj - j0, k0 = fk | 0, tk = fk - k0, k1 = k0 === nzm ? k0 : k0 + 1;
        let i1 = i0 + 1, j1 = j0 + 1;
        if (L.wrap) { i0 = ((i0 % n) + n) % n; i1 = ((i1 % n) + n) % n; j0 = ((j0 % n) + n) % n; j1 = ((j1 % n) + n) % n; }
        else { i0 = i0 < 0 ? 0 : i0 > n - 1 ? n - 1 : i0; i1 = i1 > n - 1 ? n - 1 : i1; j0 = j0 < 0 ? 0 : j0 > n - 1 ? n - 1 : j0; j1 = j1 > n - 1 ? n - 1 : j1; }
        const b00 = off + k0 * per, b10 = off + k1 * per, r0 = j0 * n, r1 = j1 * n;
        const c000 = lut[vol[b00 + r0 + i0]!]!, c100 = lut[vol[b00 + r0 + i1]!]!, c010 = lut[vol[b00 + r1 + i0]!]!, c110 = lut[vol[b00 + r1 + i1]!]!;
        const c001 = lut[vol[b10 + r0 + i0]!]!, c101 = lut[vol[b10 + r0 + i1]!]!, c011 = lut[vol[b10 + r1 + i0]!]!, c111 = lut[vol[b10 + r1 + i1]!]!;
        const e0 = (c000 + (c100 - c000) * ti) + ((c010 + (c110 - c010) * ti) - (c000 + (c100 - c000) * ti)) * tj;
        const e1 = (c001 + (c101 - c001) * ti) + ((c011 + (c111 - c011) * ti) - (c001 + (c101 - c001) * ti)) * tj;
        const q = e0 + (e1 - e0) * tk;                       // the byte (the root of the extinction) read trilinearly, then squared and raised to `steep`, as the volume's shader reads it (readD, d * d, pow)
        if (q > 0) { sum += (lin ? q * q : cube ? q * q * q : Math.pow(q, p2)) * em * ds; if (sum > cap) return sum; }
      }
    }
    s += ds;
  }
  return sum;
}

/** The sun's and the sky's light with the cloud's optical depth in: `e` = exp(-tau) on the ray to the sun (the beam's mean transmittance), `tau` its optical depth,
 *  `dome` the cloud's factor on skylight, `mu` the sine of the sun's elevation (0 with the sun down). Writes `direct` (the beam normal to itself) and `sky`
 *  (the diffuse light on an open horizontal surface), the units of the sun message's direct_rgb and sky_rgb. */
export function applyFilter(atm: Atm, clear: ArrayLike<number>, mu: number, e: number, tau: number, dome: ArrayLike<number>, direct: number[], sky: number[]): void {
  cloudOptics.beamToDiffuse(LIQUID, tau * Math.max(mu, 0), Math.max(mu, 0.08), BTR);     // (the table's axis is the beam's cosine; the light core clamps it at 0.08 too)
  for (let i = 0; i < 3; i++) {
    const c = clear[i]!;
    direct[i] = c * atm.tbh * e;
    sky[i] = atm.open[i]! * atm.mh * dome[i]! + c * mu * (atm.sc * dome[i]! + atm.tbh * BTR[i]!) + atm.floor[i]!;
  }
}
const BTR = [0, 0, 0];

export class SkyLight {
  private lut = new Float32Array(256);
  private lutRev = -1;
  private lastBeam = -1e9; private lastDome = -1e9; private lastMoon = -1e9;
  private domeF = new Float32Array(DOME_RAYS * 3).fill(1);      // each ray's share of the sky's light the cloud passes (a colour), at its last march
  private domeS = new Float32Array(DOME_RAYS * 3).fill(1);      // the same, eased (what the directional light is made from)
  private tmp3 = [1, 1, 1];
  private domePhase = 0; private domeReady = false;
  // the clear sky's shape over the dome (skyView.ts shapeAt: the sky-view table over its own irradiance, three channels, each with a cosine weighted mean of 1), read at each ray
  private shapeFn: ShapeFn | null = null; private shapeTmp = [1, 1, 1];
  private shp = new Float32Array(DOME_RAYS * 3).fill(1);
  private lRay = new Float32Array(DOME_RAYS * 3);
  // what the last step found, and the eased values the renderer reads
  tauRaw = 0; beamRaw = 1; domeRaw = 1;
  /** The optical depth straight up from the eye, raw and eased (one more ray with the beam's: the cloud shadow's reference for the sky's light overhead, cloudShadow.ts). */
  tauUpRaw = 0; tauUp = 0;
  beam = 1; dome = 1; tau = 0;
  /** The dome's factor per colour, raw and eased (`domeRaw` and `dome` are their luminance, for readouts). */
  domeRawRgb = [1, 1, 1]; domeRgb = [1, 1, 1];
  /** The moon's beam through the cloud, when asked for (MoonIn.march): the mean transmittance on its rays, raw and eased, and its optical depth. 1 and 0 while it is not marched. */
  moonRaw = 1; moonBeam = 1; moonTau = 0; moonTauRaw = 0;
  private moonFresh = true; private moonGlow = 0; private moonD = new Float32Array(3);
  /** Whether the moon's beam has been worked through the volume since it was last off (until then `moonBeam` is not a measurement). */
  get moonMarched(): boolean { return !this.moonFresh; }
  ready = false;
  /** Cost of a piece of work (the beam's five rays, or a quarter of the dome), ms: the last, a running mean and the worst since the last `resetCost`. */
  costLast = 0; costMean = 0; costMax = 0; steps = 0;
  /** The rays marched in the last piece of work (for the report). */
  lastRays = 0;
  private lastNow = -1;

  resetCost() { this.costMax = 0; }

  /** The clear sky's shape (the sky-view table, read through a function so a table the sky solver rewrites is followed): for a ray's sine of elevation and its azimuth from the sun (0..pi) it fills a colour whose cosine weighted mean over the sky is 1. */
  setShape(fn: ShapeFn | null) { this.shapeFn = fn; }

  /** Drop what was learnt (the volume went away, or the server's light is wanted): the next call starts afresh. */
  reset() { this.ready = false; this.domeReady = false; this.lastBeam = this.lastDome = this.lastMoon = -1e9; this.lastNow = -1; this.moonFresh = true; }

  private table() {
    if (this.lutRev >= 0) return;
    for (let i = 0; i < 256; i++) this.lut[i] = i / 255;          // the byte as a share of its maximum (the shader's own unit); extMax, the square and the exponent come after the interpolation
    this.lutRev = 1;
  }

  /** The mean transmittance of the cloud on a beam from a body at unit direction `dir` with apparent radius `ang`: its centre and four rays on a ring about it (the penumbra: the shadow's edge is a ramp, not a flip). */
  private beamMean(v: VolView, ex: number, ey: number, ez: number, dir: ArrayLike<number>, ang: number): number {
    const sx = dir[0]!, sy = dir[1]!, sz = dir[2]!;
    // two unit vectors across the beam
    let ax = sz, ay = 0, az = -sx; const al = Math.hypot(ax, az);
    if (al < 1e-4) { ax = 1; az = 0; } else { ax /= al; az /= al; }
    const bx = sy * az - sz * ay, by = sz * ax - sx * az, bz = sx * ay - sy * ax;
    const r = Math.max(RING_RAD, 1.5 * ang);
    let T = 0;
    for (let k = 0; k < 5; k++) {
      let dx = sx, dy = sy, dz = sz;
      if (k > 0) {
        const ph = (k - 1) * 1.5707963 + 0.7853982, c = Math.cos(ph) * r, s = Math.sin(ph) * r;
        dx += c * ax + s * bx; dy += c * ay + s * by; dz += c * az + s * bz;
        const l = 1 / Math.hypot(dx, dy, dz); dx *= l; dy *= l; dz *= l;
      }
      T += Math.exp(-rayTau(v, this.lut, ex, ey, ez, dx, dy, dz, (k * 0.382) % 1, TAU_CAP_BEAM));
    }
    return T / 5;
  }

  /** Each dome ray's share of the clear sky's brightness: the sky model's table read at the ray, for the sun where it is now, and a glow about the moon (the same kind of forward lobe, weighted by `moonGlow`). */
  private skyShape(sun: ArrayLike<number>) {
    const fn = this.shapeFn, t = this.shapeTmp;
    const sunAz = Math.atan2(sun[2]!, sun[0]!);
    for (let i = 0; i < DOME_RAYS; i++) {
      if (fn) {
        let d = Math.abs(RAY_AZ[i]! - sunAz); d -= 2 * PI * Math.floor(d / (2 * PI)); if (d > PI) d = 2 * PI - d;        // the angle between the ray's azimuth and the sun's, 0..pi
        fn(RAY_MU[i]!, d, t);
        this.shp[i * 3] = t[0]!; this.shp[i * 3 + 1] = t[1]!; this.shp[i * 3 + 2] = t[2]!;
      } else { this.shp[i * 3] = 1; this.shp[i * 3 + 1] = 1; this.shp[i * 3 + 2] = 1; }
      if (this.moonGlow > 0) {
        const cs = RAY_X[i]! * this.moonD[0]! + RAY_MU[i]! * this.moonD[1]! + RAY_Z[i]! * this.moonD[2]!;
        const w = MOON_GLOW * this.moonGlow * Math.exp(-Math.acos(Math.min(1, Math.max(-1, cs))) / MOON_GLOW_RAD);
        for (let c = 0; c < 3; c++) this.shp[i * 3 + c] = this.shp[i * 3 + c]! + w;
      }
    }
  }

  /**
   * Once a frame. `v` is the volume as it is drawn now (null: none, and `ready` goes false so the caller keeps the server's light), `eye` the place (three axes, metres
   * above the sea), `sun` the unit vector to the sun (three axes), `up` whether the sun is above the horizon, `ang` its apparent radius in radians.
   * The beam is redone every STEP_MS and a quarter of the dome every STEP_MS, never in the same frame (so no frame pays for both); between them it only eases.
   * The first call does the whole of both. Returns `ready`.
   */
  update(nowMs: number, v: VolView | null, eye: ArrayLike<number>, sun: ArrayLike<number>, up: boolean, ang: number, moon: MoonIn | null = null): boolean {
    if (!v || v.layers.length === 0 || !cloudOptics.ready) { this.reset(); return false; }
    const dt = this.lastNow < 0 ? 1 : Math.min(2, Math.max(0, (nowMs - this.lastNow) / 1000));
    this.lastNow = nowMs;
    const first = !this.ready;
    // what the moon is doing this call (the sky's shape wants its glow every dome step; its beam is marched only when asked for and while it is up)
    this.moonGlow = moon && moon.up ? Math.max(0, moon.glow) : 0;
    if (moon) { this.moonD[0] = moon.dir[0]!; this.moonD[1] = moon.dir[1]!; this.moonD[2] = moon.dir[2]!; }
    const moonOn = !!moon && moon.march && moon.up;
    const beamDue = first || nowMs - this.lastBeam >= STEP_MS, domeDue = !this.domeReady || nowMs - this.lastDome >= STEP_MS;
    const moonDue = moonOn && (first || nowMs - this.lastMoon >= STEP_MS);
    let doBeam = beamDue, doDome = domeDue, doMoon = moonDue;
    if (!first) {                                           // more than one due (a slow frame, or the moon): one piece of work a frame, the one that has waited longest; the others follow in the next frames
      let best = -1, bt = Infinity;
      if (beamDue && this.lastBeam < bt) { best = 0; bt = this.lastBeam; }
      if (domeDue && this.lastDome < bt) { best = 1; bt = this.lastDome; }
      if (moonDue && this.lastMoon < bt) { best = 2; bt = this.lastMoon; }
      doBeam = best === 0; doDome = best === 1; doMoon = best === 2;
    }
    if (!moonOn) { this.moonRaw = 1; this.moonTauRaw = 0; this.moonFresh = true; }
    if (doBeam || doDome || doMoon) {
      const t0 = performance.now();
      this.table();
      const ex = eye[0]!, ey = eye[1]!, ez = eye[2]!;
      let rays = 0;
      if (doBeam) {
        this.lastBeam = nowMs;
        if (up) {
          const T = this.beamMean(v, ex, ey, ez, sun, ang);
          rays += 5;
          this.beamRaw = T; this.tauRaw = -Math.log(Math.max(T, 1e-12));
        } else { this.beamRaw = 1; this.tauRaw = 0; }
        this.tauUpRaw = rayTau(v, this.lut, ex, ey, ez, 0, 1, 0, 0.5, 60, DOME_RANGE_M); rays += 1;
      }
      if (doMoon && moon) {
        this.lastMoon = nowMs;
        const T = this.beamMean(v, ex, ey, ez, moon.dir, moon.ang);
        rays += 5;
        this.moonRaw = T; this.moonTauRaw = -Math.log(Math.max(T, 1e-12));
        if (this.moonFresh) { this.moonBeam = T; this.moonFresh = false; }
      }
      if (doDome) {
        // a quarter of the dome's rays each time, every fourth one round the ring (so each quarter spans the whole range of heights); never all of them at once, not even the first time:
        // that time the rays not yet done take the mean of the ones that are, and the eased dome catches up over the next few steps
        const phase = this.domePhase;
        const part = [0, 0, 0]; let wsum = 0;
        for (let k = 0; k < DOME_PER_STEP; k++) {
          const i = 4 * k + phase, mu = RAY_MU[i]!;
          const tau = rayTau(v, this.lut, ex, ey, ez, RAY_X[i]!, mu, RAY_Z[i]!, ((i * 0.618) % 1), cloudOptics.tauMax / mu, DOME_RANGE_M);   // tau_v = tau * mu: the table ends at tauMax, beyond it a layer passes nothing
          cloudOptics.diffuseTransmittance(LIQUID, tau * mu, this.tmp3);
          for (let c = 0; c < 3; c++) { this.domeF[i * 3 + c] = this.tmp3[c]!; part[c] = part[c]! + mu * this.tmp3[c]!; }
          wsum += mu;
          rays++;
        }
        if (!this.domeReady) {                                              // the first time: the rays not yet done take the mean of the ones that are
          for (let i = 0; i < DOME_RAYS; i++) if (i % 4 !== phase) for (let c = 0; c < 3; c++) this.domeF[i * 3 + c] = part[c]! / wsum;
          this.domeS.set(this.domeF);
        }
        this.skyShape(sun);
        this.domePhase = (phase + 1) % 4;
        this.domeReady = true;
        this.lastDome = first ? nowMs - 0.5 * STEP_MS : nowMs;           // the first time, the next quarter falls half a step after the beam's next
        for (let c = 0; c < 3; c++) {
          let sum = 0;
          for (let i = 0; i < DOME_RAYS; i++) sum += RAY_MU[i]! * this.domeF[i * 3 + c]!;
          this.domeRawRgb[c] = sum / (0.5 * DOME_RAYS);                      // the cosine weighted mean (the weights, mu, sum to N / 2)
        }
        this.domeRaw = 0.2126 * this.domeRawRgb[0]! + 0.7152 * this.domeRawRgb[1]! + 0.0722 * this.domeRawRgb[2]!;
      }
      if (first) { this.beam = this.beamRaw; this.tauUp = this.tauUpRaw; this.dome = this.domeRaw; for (let c = 0; c < 3; c++) this.domeRgb[c] = this.domeRawRgb[c]!; }
      this.ready = true;
      const ms = performance.now() - t0;
      this.costLast = ms; this.costMean = this.steps ? 0.95 * this.costMean + 0.05 * ms : ms; if (ms > this.costMax) this.costMax = ms;
      this.steps++; this.lastRays = rays;
    }
    // ease toward the last values (the transmittance in linear terms: a shadow's edge is a ramp over a moment, not a flip)
    const kb = 1 - Math.exp(-dt / BEAM_SMOOTH_S), kd = 1 - Math.exp(-dt / DOME_SMOOTH_S);
    this.beam += (this.beamRaw - this.beam) * kb;
    this.tauUp += (this.tauUpRaw - this.tauUp) * kb;
    this.moonBeam += (this.moonRaw - this.moonBeam) * kb;
    for (let c = 0; c < 3; c++) this.domeRgb[c] = this.domeRgb[c]! + (this.domeRawRgb[c]! - this.domeRgb[c]!) * kd;
    this.dome = 0.2126 * this.domeRgb[0]! + 0.7152 * this.domeRgb[1]! + 0.0722 * this.domeRgb[2]!;
    for (let i = 0; i < DOME_RAYS * 3; i++) { const x = this.domeS[i]!; this.domeS[i] = x + (this.domeF[i]! - x) * kd; }       // each ray eased as the dome factor is
    this.moonTau = -Math.log(Math.max(this.moonBeam, 1e-12));
    this.tau = -Math.log(Math.max(this.beam, 1e-12));
    return this.ready;
  }

  /** The light at the eye from the eased beam and dome and the sun message's pieces: fills `direct` and `sky` (see applyFilter). */
  light(atm: Atm, clear: ArrayLike<number>, mu: number, direct: number[], sky: number[]) {
    applyFilter(atm, clear, mu, this.beam, this.tau, this.domeRgb, direct, sky);
  }

  /**
   * The ambient light as a function of direction: spherical harmonics up to the second band (nine coefficients, the form three's LightProbe takes), in the units of the
   * flat ambient.
   *
   * The sky is the dome's rays, each with a radiance
   *     L_c = A_c f_c shape_c + B_c u_c / mean(u_c)    f_c = the ray's share of the sky's light the cloud passes (cloudOptics.diffuseTransmittance at tau_v), shape_c = the clear sky's brightness in that direction (a mean of 1),
   *     u_c = ((1 - f_c) + eps)(1 + 2 mu)              A = open mh + clear mu sc, B = clear mu tbh btr + floor: the two parts of the server's sky light (see applyFilter),
   * the open sky seen through gaps and the cloud's own scattered light, which is brightest where the cloud is thick and (as under an overcast sky) toward the zenith.
   * The sky is scaled so that a face turned up gets exactly `E` (the flat ambient's irradiance, per channel): the sky's light only moves around between directions, and
   * level ground is lit as it was. Below the horizon the ground is a uniform radiance rho H / pi (`H` = the irradiance the open ground gets from sky and sun, `rho` its
   * albedo), which lights the faces that look down. `amt` blends the result with the flat ambient (0 = flat, 1 = all of it).
   *
   * `out` takes nine rgb triples in three's order: the constant band; the terms along y (up), z and x; then xy, yz, 3z^2 - 1, xz and x^2 - y^2. A face's irradiance is
   * what faceLight gives. It never comes out below zero (the directional terms are scaled back if a face would).
   */
  field(atm: Atm, clear: ArrayLike<number>, mu: number, E: ArrayLike<number>, H: ArrayLike<number>, rho: number, amt: number, out: Float32Array | number[]): void {
    const N = DOME_RAYS;
    cloudOptics.beamToDiffuse(LIQUID, this.tau * Math.max(mu, 0), Math.max(mu, 0.08), BTR);
    const u = this.lRay;                                   // the cloud-lit part's weight over the dome, from the same rays (the first N entries)
    const dw = 2 * PI / N;
    const S = this.sh9;
    for (let c = 0; c < 3; c++) {
      let su = 0;
      for (let i = 0; i < N; i++) { const w = ((1 - this.domeS[i * 3 + c]!) + CLOUD_EPS) * (1 + 2 * RAY_MU[i]!); u[i] = w; su += RAY_MU[i]! * w; }
      const ubar = su / (0.5 * N);
      const A = atm.open[c]! * atm.mh + clear[c]! * mu * atm.sc;
      const B = (clear[c]! * mu * atm.tbh * BTR[c]! + atm.floor[c]!) / ubar;
      let sM = 0, s0 = 0, sx = 0, sy = 0, sz = 0, sxy = 0, syz = 0, sz2 = 0, sxz = 0, sxx = 0;
      for (let i = 0; i < N; i++) {
        const L = A * this.domeS[i * 3 + c]! * this.shp[i * 3 + c]! + B * u[i]!;
        const x = RAY_X[i]!, y = RAY_MU[i]!, z = RAY_Z[i]!;
        sM += y * L; s0 += L; sx += L * x; sy += L * y; sz += L * z; sxy += L * x * y; syz += L * y * z; sz2 += L * (3 * z * z - 1); sxz += L * x * z; sxx += L * (x * x - y * y);
      }
      const M = sM / (0.5 * N), flat0 = E[c]! / 0.886227;
      for (let k = 0; k < 9; k++) out[k * 3 + c] = k === 0 ? flat0 : 0;
      if (!(M > 1e-30)) continue;
      const Lbar = E[c]! / PI, Lg = rho * H[c]! / PI, kk = Lbar / M * dw;       // kk: the sky's scale (its cosine weighted mean is Lbar) and the solid angle of a ray
      S[0] = 0.282095 * s0 * kk; S[1] = 0.488603 * sy * kk; S[2] = 0.488603 * sz * kk; S[3] = 0.488603 * sx * kk;
      S[4] = 1.092548 * sxy * kk; S[5] = 1.092548 * syz * kk; S[6] = 0.315392 * sz2 * kk; S[7] = 1.092548 * sxz * kk; S[8] = 0.546274 * sxx * kk;
      // the bands left out leave the up face a percent or so off: put it right (the sky only; the ground's own up face is zero)
      const up = 0.886227 * S[0]! + 1.023328 * S[1]! - 0.247708 * S[6]! - 0.429043 * S[8]!;
      if (up > 0) { const q = E[c]! / up; for (let k = 0; k < 9; k++) S[k] = S[k]! * q; }
      // the ground: a radiance Lg over the lower half is a constant band and a term along down
      S[0] = S[0]! + 0.282095 * 2 * PI * Lg; S[1] = S[1]! - 0.488603 * PI * Lg;
      // a face must never come out below zero: scale the directional terms back so that the darkest of fourteen faces is not
      const c0t = 0.886227 * S[0]!;
      let lo = c0t;
      for (let f = 0; f < 14; f++) { const v = SkyLight.irr(S, FACE[f * 3]!, FACE[f * 3 + 1]!, FACE[f * 3 + 2]!); if (v < lo) lo = v; }
      const q = lo >= 0 ? 1 : c0t / (c0t - lo);
      for (let k = 1; k < 9; k++) S[k] = S[k]! * q;
      for (let k = 0; k < 9; k++) out[k * 3 + c] = flat0 * (k === 0 ? 1 - amt : 0) + amt * S[k]!;
    }
  }
  private sh9 = new Float64Array(9);

  /** A face's irradiance from the nine coefficients of one channel (as three's shader reads them). */
  private static irr(S: ArrayLike<number>, x: number, y: number, z: number): number {
    return 0.886227 * S[0]! + 1.023328 * (S[1]! * y + S[2]! * z + S[3]! * x) + 0.858086 * (S[4]! * x * y + S[5]! * y * z + S[7]! * x * z) + S[6]! * (0.743125 * z * z - 0.247708) + 0.429043 * S[8]! * (x * x - y * y);
  }

  /** The irradiance a face turned toward the unit vector (x, y, z) gets from a field made by `field` (nine rgb triples), per channel, into `out`. */
  static faceLight(sh: ArrayLike<number>, x: number, y: number, z: number, out: number[]): void {
    for (let c = 0; c < 3; c++) {
      for (let k = 0; k < 9; k++) this.chan[k] = sh[k * 3 + c]!;
      out[c] = Math.max(0, SkyLight.irr(this.chan, x, y, z));
    }
  }
  private static chan = new Float64Array(9);

  describe(): string {
    return `sky light (volume): tau ${this.tau.toFixed(2)} beam ${this.beam.toFixed(3)} dome ${this.dome.toFixed(3)} (raw ${this.tauRaw.toFixed(2)} / ${this.domeRaw.toFixed(3)}); work ${this.costLast.toFixed(2)} ms (mean ${this.costMean.toFixed(2)}, max ${this.costMax.toFixed(2)}, ${this.lastRays} rays; the beam every ${STEP_MS} ms, a quarter of the dome every ${STEP_MS} ms, half a step apart)`;
  }
}
